import { MruCache } from 'owa-mru-cache';
import type { SmartPillData } from 'owa-smart-pill';
import { registerCallback, type SuggestedDraftCallbackFuncType } from './suggestedDraftsCallbacks';
import type { CopilotAddinErrorType } from 'owa-copilot-service';

const CACHE_EXPIRY = 1000 * 60 * 60; // 1 hour

interface CacheEntry {
    suggestions: SmartPillData[];
    timestamp: number;
    debugInfo: any;
    addinErrorCode?: CopilotAddinErrorType;
}

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
 *	> Function MruCache called at module scope is not allowed. This may cause unwanted side effects. */
const cache = new MruCache<CacheEntry>('SuggestedDraft', 10);
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function initialize called at module scope is not allowed. This may cause unwanted side effects. */
cache.initialize(new Map<string, CacheEntry>());

export function getCachedSuggestedDrafts(
    itemId: string,
    conversationId: string,
    scenario: string,
    callback: SuggestedDraftCallbackFuncType,
    addinTitleId: string
): [SmartPillData[], any, CopilotAddinErrorType | undefined] {
    const key = getCacheKey(itemId, conversationId, addinTitleId);
    registerCallback(scenario, callback);

    if (key && cache.has(key)) {
        const cachedSuggestionsEntry = cache.getAndTouch(key);

        if (
            cachedSuggestionsEntry &&
            Date.now() - cachedSuggestionsEntry.timestamp < CACHE_EXPIRY
        ) {
            cachedSuggestionsEntry.debugInfo.suggestionsSource = 'Cache';
            return [
                cachedSuggestionsEntry.suggestions,
                cachedSuggestionsEntry.debugInfo,
                cachedSuggestionsEntry.addinErrorCode,
            ];
        } else {
            cache.remove(key);
        }
    }

    return [[], {}, undefined];
}

export function addSuggestedDraftsToCache(
    itemId: string,
    conversationId: string,
    suggestions: SmartPillData[],
    debugInfo: any,
    addinTitleId: string,
    addinErrorCode?: CopilotAddinErrorType
): void {
    const key = getCacheKey(itemId, conversationId, addinTitleId);

    if (key && suggestions.length > 0) {
        cache.add(key, {
            suggestions,
            timestamp: Date.now(),
            debugInfo,
            addinErrorCode,
        });
    }
}

function getCacheKey(itemId: string, conversationId: string, addinTitleId: string): string {
    return `${conversationId}-${itemId}-${addinTitleId}`;
}
